import React from 'react';
import {
  Box,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTileColours } from './tileTheme';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Above this many spouses, chips would wrap past one row at the real sidebar
// width, so a Select is the smaller control.
//
// The mock proposed 4, but it was drawn at 452px. The sidebar is 400px
// (PedigreeSideBar.jsx:69); after the container padding and the "with" prefix
// roughly 330px is left, which three "Person 12"-width chips plus gaps fill. If
// the real chips measure narrower, raise this — the rule is "chips while they fit
// on one row", not the number itself.
export const MAX_CHIP_SPOUSES = 3;

// Applied to each ToggleButton directly. A descendant rule from the group
// ('& .MuiToggleButton-root': { '&.Mui-selected': … }) is the same specificity as
// MUI's own selected rule and loses on insertion order — which left the selected
// chip in MUI's default action.selected grey, indistinguishable from the unselected
// ones inside the already-grey scope container.
// '&&' doubles the emotion class, which beats MUI's own single-class grouped rules
// regardless of stylesheet order — ToggleButtonGroup otherwise butts its children
// together with a negative margin and squares off the inner corners, turning three
// chips into one segmented block where the unselected pair read as static text.
//
// The selected chip is filled with the DARKENED teal, not the brand teal: white
// 12px text on brand teal is about 2.9:1, well under the floor. Same split, same
// reason, as SidebarSection's active label.
const chipSx = (c) => ({
  '&&': {
    m: 0,
    px: 1.25,
    py: 0.375,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '999px',
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    color: c.restText
  },
  '&&:hover': { bgcolor: c.hoverTint, borderColor: c.hoverBorder },
  '&&.Mui-selected': {
    bgcolor: c.selectedFill,
    borderColor: c.selectedFill,
    color: '#fff'
  },
  '&&.Mui-selected:hover': {
    bgcolor: c.selectedFill,
    borderColor: c.selectedFill
  }
});

/**
 * Chooses WHICH union a child is added to. It exists to disambiguate, so when
 * there is nothing to disambiguate it does not render at all.
 *
 *   0 spouses  → no selector. A single-parent child is legitimate and still
 *                supported, so the child tiles stay ENABLED; the line just says
 *                what will happen.
 *   1 spouse   → no selector. A control with one option is a wasted click and
 *                wasted height; just name them.
 *   2–3        → chips, so the child scope is readable at rest. That was the
 *                original problem with this panel: a Select showed the scope only
 *                when opened.
 *   4+         → a Select.
 *
 * VALUES ARE PIDS, NOT REFS. This is the one thing to be careful about here.
 * `spouseOptionsFor` returns BOTH: `ref` (`_id` once the member has been saved,
 * else the pid) is what `otherParentMemberId` stores and what PregnancyModal
 * wants, while `pid` is what `addChildrenToRecords` needs — it matches
 * `coParentPid` against `spouseIds`, which holds pids. The first pass keyed these
 * controls on `ref` while the container passed and compared a pid, so on a real
 * saved family no chip ever showed as selected and every child silently fell back
 * to the most recent spouse. It survived the tests only because the fixtures had
 * no `_id`, which made `ref` collapse to `pid`.
 *
 * Isolated because these rules are the fiddliest part of the panel, and because
 * it is the seam a future one-row-per-spouse layout swaps out.
 *
 * @param {Object} props
 * @param {Array<{ref: (string|number), pid: number, label: string, gender: string}>} props.options
 *        Ordered as `spouseIds`, from spouseOptionsFor — the same derivation the
 *        Reproductive section consumes, so the labels match another real surface.
 * @param {?number} props.value    A PID.
 * @param {Function} props.onChange  Called with a PID.
 * @param {boolean} [props.disabled]
 */
const SpouseSelector = ({ options = [], value, onChange, disabled }) => {
  const t = useAutoTranslation();

  const c = useTileColours();
  const CHIP_SX = chipSx(c);
  const MutedLine = ({ children }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mb: 1,
        minWidth: 0
      }}
    >
      <InfoOutlinedIcon
        aria-hidden="true"
        sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }}
      />
      <Typography
        sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }}
      >
        {children}
      </Typography>
    </Box>
  );

  if (options.length === 0) {
    return (
      <MutedLine>
        {t('No spouse recorded — this child will have one recorded parent.')}
      </MutedLine>
    );
  }

  if (options.length === 1) {
    return (
      <MutedLine>
        {t('With {{label}}, the only spouse recorded', {
          label: options[0].label
        })}
      </MutedLine>
    );
  }

  const Prefix = () => {
    const t = useAutoTranslation();
    return (
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          flexShrink: 0,
          mr: 0.75
        }}
      >
        {t('with')}
      </Typography>
    );
  };

  if (options.length <= MAX_CHIP_SPOUSES) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, minWidth: 0 }}>
        <Prefix />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={value ?? null}
          aria-label={t('Add children with')}
          disabled={disabled}
          // A null value would clear the scope and silently retarget the child
          // tiles, so ignore a deselect click.
          onChange={(_e, next) => next != null && onChange(next)}
          // The group is kept purely for its roving arrow-key focus; the pill
          // appearance is entirely in CHIP_SX.
          sx={{ flexWrap: 'wrap', gap: 0.75 }}
        >
          {options.map((o) => (
            <ToggleButton key={o.pid} value={o.pid} sx={CHIP_SX}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, minWidth: 0 }}>
      <Prefix />
      <Select
        size="small"
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ 'aria-label': t('Add children with') }}
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 12.5,
          bgcolor: 'background.paper'
        }}
      >
        {options.map((o) => (
          <MenuItem key={o.pid} value={o.pid} sx={{ fontSize: 12.5 }}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default SpouseSelector;
